/**
 * Quick-add-material modal. Sibling to AddEmployeeModal / AddEquipmentModal —
 * same chrome, same "+ New X" trigger pattern. Lets Joey add a genuinely new
 * material to the project's roster (Settings → Materials) without leaving the
 * inbox. The created row joins materials_master, so it shows up in every
 * materials picker from then on (this is the intended way the roster grows).
 *
 * Pre-fills are blank here — unlike InboxMaterialsList's inline "add new
 * material" form (which prefills from the foreman's quoted PDF text), this
 * modal is reached from the generic "+ Add material" line, which has no PDF
 * text to draw from.
 */
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { MoneyInput } from './MoneyInput';
import type { MaterialWriteRequest, SettingsMaterial } from '@coffman-tm/shared';
import { useBodyScrollLock } from '../lib/useBodyScrollLock';

export function AddMaterialModal({
  open,
  initialName,
  initialUnit,
  onClose,
  onAdded,
}: {
  open: boolean;
  initialName: string;
  initialUnit: string;
  onClose: () => void;
  onAdded: (material: SettingsMaterial) => void | Promise<void>;
}) {
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [rateDollars, setRateDollars] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setUnit(initialUnit);
    setRateDollars('');
    setBusy(false);
    setErr(null);
  }, [open, initialName, initialUnit]);

  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const submit = async () => {
    if (!token) return;
    if (!name.trim() || !unit.trim()) {
      setErr('Name and unit are required.');
      return;
    }
    const rate = Number(rateDollars || '0');
    if (!Number.isFinite(rate) || rate < 0) {
      setErr('Default rate must be a non-negative number.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const body: MaterialWriteRequest = {
        name: name.trim(),
        unit: unit.trim(),
        default_rate_cents: Math.round(rate * 100),
        active: true,
      };
      const created = await api.settings.createMaterial(token, body);
      await onAdded(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to add material');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="pdf-modal-overlay"
      onClick={() => {
        if (!busy) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Add material"
    >
      <div className="new-pco-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-head">
          <div className="pdf-modal-title">Add material</div>
          <button
            type="button"
            className="pdf-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        </div>
        <div className="new-pco-body">
          <div className="new-pco-field">
            <label className="field-label">Name</label>
            <input
              className="field-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              disabled={busy}
              placeholder='e.g. Roadbase 3/4"'
            />
          </div>
          <div className="new-pco-row">
            <div className="new-pco-field">
              <label className="field-label">Unit</label>
              <input
                className="field-input"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                disabled={busy}
                placeholder="ton, yd, ea…"
              />
            </div>
            <div className="new-pco-field">
              <label className="field-label">Default rate (/{unit.trim() || 'unit'})</label>
              <MoneyInput
                className="field-input mono"
                value={rateDollars}
                onChange={setRateDollars}
                disabled={busy}
              />
            </div>
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            This joins the project's materials list (Settings → Materials) so it's
            available to pick next time.
          </div>
          {err && <div style={{ color: 'var(--danger)', fontSize: 12 }}>{err}</div>}
          <div className="new-pco-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="btn btn-cta" onClick={submit} disabled={busy}>
              {busy ? 'Adding…' : 'Add material'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
